import { c as _c } from "react/compiler-runtime";
import { IcoChevronDown16 } from '@onefootprint/icons';
import * as Collapsible from '@radix-ui/react-collapsible';
import { cx } from 'class-variance-authority';
import type { ComponentType, ReactNode } from 'react';
import { useState } from 'react';
type OpenState = {
  open?: never;
  defaultExpanded?: boolean;
  /** Resolving to false keeps the section as it is. */
  onOpenChange?: (open: boolean) => boolean | Promise<boolean>;
} | {
  /** Owned by a parent that also opens the section from outside, such as on a preview click. */
  open: boolean;
  defaultExpanded?: never;
  onOpenChange: (open: boolean) => void;
};
type SettingsSectionProps = {
  title: string;
  icon: ComponentType<{
    className?: string;
  }>;
  children: ReactNode;
  /** Controls drawn before the chevron. They sit outside the trigger, since a button can't hold one. */
  actions?: ReactNode;
  /** Keeps the fields mounted while shut, since react-hook-form stops validating a field that unmounts. */
  keepMounted?: boolean;
} & OpenState;
const Chevron = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <IcoChevronDown16 className={cx("size-4 shrink-0 transition-transform duration-200", "group-data-[state=open]:rotate-180")} />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const SettingsSection = t0 => {
  const $ = _c(37);
  let Icon;
  let actions;
  let children;
  let keepMounted;
  let openState;
  let title;
  if ($[0] !== t0) {
    ({
      title,
      icon: Icon,
      children,
      actions,
      keepMounted,
      ...openState
    } = t0);
    $[0] = t0;
    $[1] = Icon;
    $[2] = actions;
    $[3] = children;
    $[4] = keepMounted;
    $[5] = openState;
    $[6] = title;
  } else {
    Icon = $[1];
    actions = $[2];
    children = $[3];
    keepMounted = $[4];
    openState = $[5];
    title = $[6];
  }
  const [uncontrolledOpen, setUncontrolledOpen] = useState(openState.defaultExpanded ?? false);
  const open = openState.open ?? uncontrolledOpen;
  let t1;
  if ($[7] !== openState) {
    t1 = async newOpen => {
      if (openState.open !== undefined) {
        openState.onOpenChange(newOpen);
      } else {
        if (openState.onOpenChange) {
          const shouldChange = await openState.onOpenChange(newOpen);
          if (shouldChange) {
            setUncontrolledOpen(newOpen);
          }
        } else {
          setUncontrolledOpen(newOpen);
        }
      }
    };
    $[7] = openState;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const handleOpenChange = t1;
  let t2;
  if ($[9] !== Icon) {
    t2 = <Icon className="size-4 shrink-0" />;
    $[9] = Icon;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== title) {
    t3 = <span className="flex-1 text-label-1 text-primary">{title}</span>;
    $[11] = title;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== actions) {
    t4 = !actions && <Chevron />;
    $[13] = actions;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== t2 || $[16] !== t3 || $[17] !== t4) {
    t5 = <Collapsible.Trigger asChild={true}><button type="button" className="flex flex-1 items-center gap-2 text-left hover:opacity-70 transition-opacity group">{t2}{t3}{t4}</button></Collapsible.Trigger>;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== actions) {
    t6 = actions && <>{actions}<Collapsible.Trigger asChild={true}><button type="button" tabIndex={-1} aria-hidden={true} className="group hover:opacity-70 transition-opacity"><Chevron /></button></Collapsible.Trigger></>;
    $[19] = actions;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== t5 || $[22] !== t6) {
    t7 = <div className="flex items-center gap-2 px-4 py-3.5">{t5}{t6}</div>;
    $[21] = t5;
    $[22] = t6;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  const t8 = keepMounted || undefined;
  let t9;
  if ($[24] !== keepMounted) {
    t9 = cx("data-[state=closed]:animate-collapseUp data-[state=open]:animate-collapseDown", {
      "data-[state=closed]:hidden": keepMounted
    });
    $[24] = keepMounted;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== children) {
    t10 = <div className="flex flex-col gap-4 px-4 pb-4">{children}</div>;
    $[26] = children;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== t10 || $[29] !== t8 || $[30] !== t9) {
    t11 = <Collapsible.Content forceMount={t8} className={t9}>{t10}</Collapsible.Content>;
    $[28] = t10;
    $[29] = t8;
    $[30] = t9;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== handleOpenChange || $[33] !== open || $[34] !== t11 || $[35] !== t7) {
    t12 = <Collapsible.Root open={open} onOpenChange={handleOpenChange}>{t7}{t11}</Collapsible.Root>;
    $[32] = handleOpenChange;
    $[33] = open;
    $[34] = t11;
    $[35] = t7;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  return t12;
};
export default SettingsSection;